import { useQuery } from "@tanstack/react-query";

import { V2_API_URL } from "@/lib/v2-api/client";

// Presigned URLs live 1h server-side (getPresignedUrl expiresIn=3600). Cache
// well under that so a mounted <img> never holds an expired URL.
const PRESIGN_STALE_MS = 50 * 60 * 1000;

async function fetchChatMediaUrl(mediaId: string): Promise<string | null> {
  const token = localStorage.getItem("bearer");
  const res = await fetch(`${V2_API_URL}/api/v2/chat-media/url/${mediaId}`, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  // A missing media is a legitimate "no url" — cache it as a placeholder rather
  // than treating it as a failure to retry.
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`chat-media url failed: ${res.status}`);
  const data: { url?: string } = await res.json();
  return data.url ?? null;
}

/**
 * Resolve a chat-media id to a presigned URL (V2 chat-media endpoint), cached
 * per id via TanStack Query. Consumers render a placeholder until it arrives.
 *
 * The cache is the point: the global AI assistant panel churns (open/close,
 * SSE-driven re-renders, page transitions), and the previous fetch-per-mount
 * hook re-resolved the same id hundreds of times — an N+1 storm Sentry flagged
 * on /stocks (NEXTJS-FRONTEND-42). Caching collapses those to one request per id
 * per stale window, regardless of how often the attachment remounts.
 */
export function useChatMediaUrl(mediaId: string): string | null {
  const { data } = useQuery({
    queryKey: ["chat-media-url", mediaId],
    queryFn: () => fetchChatMediaUrl(mediaId),
    enabled: Boolean(mediaId),
    staleTime: PRESIGN_STALE_MS,
    gcTime: PRESIGN_STALE_MS,
  });
  return data ?? null;
}

export async function fetchMediaBlob(url: string): Promise<Blob | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

/** Download the media behind a presigned URL under its original name. */
export async function downloadMedia(url: string, name: string): Promise<void> {
  const blob = await fetchMediaBlob(url);
  if (!blob) return;
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}
